<template>
  <main class="product-page">
    <section class="hero">
      <picture>
        <source
          media="(max-width: 767px), (orientation: portrait)"
          :srcset="phonePath('STERiJECT_01.png')"
        />
        <img
          :src="webPath('STERiJECT_01.png')"
          :alt="$t('STERiJECT.s1.body')"
          class="hero-image"
        />
      </picture>
      <div class="hero-copy">
        <h1 v-html="$t('STERiJECT.s1.title')"></h1>
        <p class="hero-name" v-html="$t('STERiJECT.s1.body')"></p>
        <p class="hero-license" v-html="$t('STERiJECT.s1.body1')"></p>
      </div>
    </section>

    <section class="intro-section">
      <h2 v-html="$t('STERiJECT.public.introTitle')"></h2>
      <p v-html="$t('STERiJECT.public.introBody')"></p>
    </section>

    <section class="feature-section">
      <article
        v-for="item in featureItems"
        :key="item.image"
        class="feature-card"
      >
        <img :src="assetPath(item.image)" :alt="localizedFeatureTitle(item)" loading="lazy" />
        <h3 v-html="localizedFeatureTitle(item)"></h3>
        <p v-html="localizedFeatureBody(item)"></p>
      </article>
    </section>

    <section class="length-section">
      <h2 v-html="$t('STERiJECT.len.title')"></h2>
      <div class="length-grid">
        <article
          v-for="item in lengthItems"
          :key="item.key"
          class="length-card"
        >
          <div class="length-chip">{{ $t(`STERiJECT.len.${item.key}.chip`) }}</div>
          <h3 v-html="$t(`STERiJECT.len.${item.key}.title`)"></h3>
          <a
            v-if="item.key === 'long'"
            href="/product/STERiGLIDE"
            class="cannula-link"
          >
            {{ $t('STERiJECT.len.cannula') }}
          </a>
          <ul>
            <li
              v-for="bodyKey in item.bodyKeys"
              :key="bodyKey"
              v-html="$t(`STERiJECT.len.${item.key}.${bodyKey}`)"
            ></li>
          </ul>
        </article>
      </div>
    </section>

    <section class="product-summary">
      <div class="summary-image">
        <img
          src="@/assets/images/product/STERiJECT/product_ject_2.png"
          :alt="$t('STERiJECT.name')"
          loading="lazy"
        />
      </div>
      <div class="summary-copy">
        <h2 v-html="$t('STERiJECT.title')"></h2>
        <p v-html="$t('STERiJECT.description')"></p>
      </div>
    </section>

    <section class="spec-section">
      <div class="spec-inner">
        <div class="spec-heading">
          <h2 v-html="$t('model')"></h2>
        </div>

        <div class="table-scroller">
          <table class="spec-table">
            <thead>
              <tr>
                <th scope="col">Gauge</th>
                <th scope="col">Code</th>
                <th scope="col">Description</th>
                <th scope="col">Inch</th>
                <th scope="col">Thin Wall</th>
                <th scope="col">Box</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(group, groupIndex) in groupedData" :key="group.gauge">
                <tr
                  v-for="(item, itemIndex) in group.items"
                  :key="item.code"
                  :class="getRowClass(displayIndex(groupIndex, itemIndex))"
                >
                  <td
                    v-if="itemIndex === 0"
                    :rowspan="group.items.length"
                    class="gauge-cell"
                  >
                    {{ group.gauge }}
                  </td>
                  <td>{{ item.code }}</td>
                  <td>{{ item.description }}</td>
                  <td>{{ item.inch }}</td>
                  <td>{{ item.utw }}</td>
                  <td>{{ item.box }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <p class="legal-note">
          {{ $t('STERiJECT.public.operator') }}<br />
          {{ $t('STERiJECT.s1.body1') }}
        </p>
      </div>
    </section>
  </main>

  <ContactFooter />
</template>

<script>
import ContactFooter from '@/components/ContactFooter.vue'

export default {
  name: 'PublicSTERiJECT',
  components: {
    ContactFooter
  },
  data() {
    return {
      featureItems: [
        {
          image: 'STERiJECT_00_1.png',
          title: 'STERiJECT.public.features.title1',
          body: 'STERiJECT.public.features.body1'
        },
        {
          image: 'STERiJECT_00_2.png',
          title: 'STERiJECT.public.features.title2',
          body: 'STERiJECT.public.features.body2'
        },
        {
          image: 'STERiJECT_00_3.png',
          title: 'STERiJECT.public.features.title3',
          body: 'STERiJECT.public.features.body3'
        }
      ],
      lengthItems: [
        { key: 'short', bodyKeys: ['body1', 'body2'] },
        { key: 'mid', bodyKeys: ['body1', 'body2', 'body3'] },
        { key: 'long', bodyKeys: ['body1', 'body2'] }
      ],
      tableData: [
        { gauge: '34G', code: 'LDS-02009', description: 'THE INViSIBLE NEEDLE 0.2 x 9mm', inch: '⅜″', utw: '', box: '100' },
        { gauge: '33G', code: 'PRE-33004', description: 'Regular Hub 33G x 4mm', inch: '³⁄₁₆″', utw: '', box: '100' },
        { gauge: '33G', code: 'PRE-33013', description: 'Regular Hub 33G x 13mm', inch: '½″', utw: '', box: '100' },
        { gauge: '32G', code: 'PRE-32004', description: 'Regular Hub 32G x 4mm', inch: '³⁄₁₆″', utw: '', box: '100' },
        { gauge: '32G', code: 'PRE-32009', description: 'Regular Hub 32G x 9mm', inch: '⅜″', utw: '', box: '100' },
        { gauge: '32G', code: 'PRE-32013', description: 'Regular Hub 32G x 13mm', inch: '½″', utw: '', box: '100' },
        { gauge: '31G', code: 'HPC-31013E', description: 'Advanced Hub 31G x 13mm', inch: '½″', utw: '√', box: '100' },
        { gauge: '30G', code: 'PRE-30004I', description: 'Regular Hub 30G x 4mm', inch: '³⁄₁₆″', utw: '√', box: '100' },
        { gauge: '30G', code: 'LDS-30009I', description: 'Low Dead Space Hub 30G x 9mm', inch: '⅜″', utw: '√', box: '100' },
        { gauge: '30G', code: 'HPC-30013I', description: 'Advanced Hub 30G x 13mm', inch: '½″', utw: '√', box: '100' },
        { gauge: '27G', code: 'HPC-27013I', description: 'Advanced Hub 27G x 13mm', inch: '½″', utw: '√', box: '100' },
        { gauge: '27G', code: 'HPC-27019I', description: 'Advanced Hub 27G x 19mm', inch: '¾″', utw: '√', box: '100' }
      ]
    }
  },
  computed: {
    isTraditionalChinese() {
      return String(this.$i18n.locale || '').toLowerCase().replace('_', '-').startsWith('zh-tw')
    },
    groupedData() {
      return this.tableData.reduce((groups, item) => {
        const group = groups.find(entry => entry.gauge === item.gauge)
        if (group) {
          group.items.push(item)
        } else {
          groups.push({ gauge: item.gauge, items: [item] })
        }
        return groups
      }, [])
    }
  },
  methods: {
    webPath(file) {
      return require(`@/assets/images/AppShell/products/STERiJECT/web/${file}`)
    },
    phonePath(file) {
      return require(`@/assets/images/AppShell/products/STERiJECT/phone/${file}`)
    },
    assetPath(file) {
      return require(`@/assets/images/AppShell/products/STERiJECT/${file}`)
    },
    localizedFeatureTitle(item) {
      return this.$t(item.title)
    },
    localizedFeatureBody(item) {
      return this.$t(item.body)
    },
    displayIndex(groupIndex, itemIndex) {
      let index = 0
      for (let i = 0; i < groupIndex; i += 1) {
        index += this.groupedData[i].items.length
      }
      return index + itemIndex
    },
    getRowClass(index) {
      return index % 2 === 0 ? 'odd-row' : 'even-row'
    }
  }
}
</script>

<style scoped>
.product-page {
  --product-blue: #0071bb;
  width: min(calc(100% - 3rem), 1320px);
  margin: 0 auto;
  color: #666;
  font-size: 1rem;
  overflow: hidden;
}

.product-page h1,
.product-page h2,
.product-page h3 {
  color: var(--product-blue) !important;
}

.hero {
  position: relative;
}

.hero-image {
  display: block;
  width: 100%;
  height: auto;
}

.hero-copy {
  position: absolute;
  top: 50%;
  left: 20%;
  transform: translate(-50%, -50%);
  width: min(30%, 390px);
  text-align: center;
  color: #222;
}

.hero-copy h1 {
  margin: 0 0 0.5rem;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: 0.02em;
}

.hero-name {
  margin: 0;
  font-size: 1.25rem;
  line-height: 1.45;
  color: #222;
}

.hero-license {
  margin: 1.25rem 0 0;
  font-size: 1rem;
  line-height: 1.9;
}

.intro-section {
  margin-top: 0;
  padding: 3rem;
  background: #f7f7f7;
  text-align: center;
}

.intro-section h2,
.length-section > h2,
.spec-heading h2 {
  margin: 0 0 1rem;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
}

.intro-section p {
  max-width: 72ch;
  margin: 0 auto;
  font-size: 1rem;
  line-height: 1.9;
}

.feature-section {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3rem;
  padding: 3rem;
}

.feature-card {
  text-align: center;
}

.feature-card img {
  display: block;
  width: min(100%, 220px);
  margin: 0 auto 1rem;
}

.feature-card h3 {
  margin: 0 0 0.65rem;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.45;
}

.feature-card p {
  margin: 0 auto;
  max-width: 38ch;
  font-size: 1rem;
  line-height: 1.9;
}

.length-section {
  padding: 3rem;
  text-align: center;
}

.length-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3rem;
  margin-top: 2rem;
}

.length-card {
  text-align: center;
}

.length-chip {
  display: grid;
  min-height: 4.5rem;
  padding: 0.75rem 1rem;
  place-items: center;
  border-radius: 0.7rem;
  background: linear-gradient(100deg, #18a8d8 0%, #d3e84b 100%);
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  white-space: pre-line;
}

.length-card h3 {
  margin: 1.1rem 0 0.45rem;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.45;
}

.length-card ul {
  padding: 0;
  margin: 0.65rem 0 0;
  list-style: none;
  line-height: 1.8;
  font-size: 1rem;
}

.cannula-link {
  color: var(--product-blue);
  font-weight: 700;
}

.product-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
  align-items: center;
  gap: 3rem;
  padding: 3rem;
}

.summary-image img {
  display: block;
  width: 100%;
  height: auto;
}

.summary-copy {
  text-align: left;
}

.summary-copy h2 {
  margin: 0 0 1.5rem;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
}

.summary-copy p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.9;
}

.spec-section {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: 3rem 0;
  background: #fff;
}

.spec-inner {
  width: min(64%, 1120px);
  margin: 0 auto;
}

.spec-heading {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.spec-heading h2 {
  margin: 0;
}

.table-scroller {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.spec-table {
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0 5px;
  color: #333;
  font-size: 1rem;
}

.spec-table th,
.spec-table td {
  padding: 0.8rem;
  border-right: 1px solid #e2e2e2;
  text-align: center;
  vertical-align: middle;
}

/* 保留各欄右側灰線，最後欄同樣保留，讓表格有完整右框 */

.spec-table thead th,
.gauge-cell {
  background: #50a7df;
  color: #fff;
  font-weight: 700;
}

.spec-table tbody td:not(.gauge-cell) {
  background: #fff;
  border-top: 1px solid #dfe3e6;
  border-bottom: 1px solid #dfe3e6;
}

.legal-note {
  margin: 2rem 0 0;
  color: #222;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.7;
  text-align: center;
}

@media (max-width: 899px), (orientation: portrait) {
  .product-page {
    width: 100%;
  }

  .hero {
    min-height: 520px;
    background: #f7f7f7;
  }

  .hero-image {
    width: 100%;
    height: 100%;
    min-height: 520px;
    object-fit: cover;
  }

  .hero-copy {
    top: 18%;
    left: 50%;
    width: min(88%, 420px);
  }

  .hero-copy h1 {
    font-size: 24px;
  }

  .hero-name {
    font-size: 1.1rem;
  }

  .feature-section {
    grid-template-columns: 1fr;
    gap: 3.5rem;
    padding-inline: 1.5rem;
  }

  .feature-card img {
    width: min(62vw, 230px);
  }

  .length-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .length-chip {
    max-width: 420px;
    margin: 0 auto;
  }

  .product-summary {
    grid-template-columns: 1fr;
    padding-inline: 1.5rem;
  }

  .summary-image img {
    width: min(100%, 540px);
    margin: 0 auto;
  }

  .summary-copy {
    text-align: center;
  }

  .spec-inner {
    width: calc(100% - 2rem);
  }

  .spec-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 575px) {
  .hero,
  .hero-image {
    min-height: 430px;
  }

  .intro-section,
  .feature-section,
  .length-section,
  .product-summary {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  .intro-section p br {
    display: none;
  }
}
</style>
